import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'; 
import API from '../services/api'; 
import html2canvas from 'html2canvas'; 
import jsPDF from 'jspdf'; 

const AlumniDashboard = () => {
    const [user, setUser] = useState(null);
    const [eventCount, setEventCount] = useState(0); 
    const navigate = useNavigate();

    // STATES FOR EDIT PROFILE MODAL
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        name: '', contact_no: '', cnic: '', wing: '', entry_year: '', leaving_year: '',
        current_status: '', education: '', institute: '',
        profession: '', job_status: '', organization: '', designation: '', // <-- Added job_status
        current_address: '', permanent_address: '',
        skills: '', achievements: '' 
    });
    const [updateStatus, setUpdateStatus] = useState('');
    const [alumniCount, setAlumniCount] = useState(0); 

    // STATES FOR PROFILE PICTURE UPLOAD & EXPORTS
    const [uploading, setUploading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false); 
    const [isPrinting, setIsPrinting] = useState(false); 
    const fileInputRef = useRef(null);
    const cardRef = useRef(null); 

    // 1. BULLETPROOF INCOMPLETE CHECK (Excludes optional skills/achievements)
    const isProfileIncomplete = React.useMemo(() => {
        if (!user) return false; 

        const isInvalid = (val) => !val || String(val).trim() === '' || String(val).toLowerCase() === 'null';

        return (
            isInvalid(user.contact_no) || 
            isInvalid(user.cnic) || 
            isInvalid(user.entry_year) || 
            isInvalid(user.leaving_year) || 
            isInvalid(user.wing) ||
            isInvalid(user.current_status) ||
            isInvalid(user.education) ||
            isInvalid(user.institute) ||
            isInvalid(user.profession) ||
            isInvalid(user.job_status) || // <-- Added Job Title check
            isInvalid(user.designation) ||
            isInvalid(user.organization) ||
            isInvalid(user.current_address) ||
            isInvalid(user.permanent_address)
        );
    }, [user]);

    // 2. FORCE MODAL TO OPEN ON LOAD IF *ANY* MANDATORY FIELD IS MISSING
    useEffect(() => {
        if (isProfileIncomplete) {
            setIsEditing(true);
        }
    }, [isProfileIncomplete]);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            navigate('/login');
        } else {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            
            setEditData({
                name: parsedUser.name || '',
                contact_no: parsedUser.contact_no || '',
                cnic: parsedUser.cnic || '',
                wing: parsedUser.wing || '',
                entry_year: parsedUser.entry_year || '',
                leaving_year: parsedUser.leaving_year || '',
                current_status: parsedUser.current_status || '',
                education: parsedUser.education || '',
                institute: parsedUser.institute || '',
                profession: parsedUser.profession || '',
                job_status: parsedUser.job_status || '', // <-- Added
                organization: parsedUser.organization || '',
                designation: parsedUser.designation || '',
                current_address: parsedUser.current_address || '',
                permanent_address: parsedUser.permanent_address || '',
                skills: parsedUser.skills || '',             
                achievements: parsedUser.achievements || ''  
            });

            // --- SILENT BACKGROUND SYNC WITH CACHE BUSTER ---
            const timestamp = new Date().getTime();
            API.get(`/get_single_alumni.php?user_id=${parsedUser.id}&t=${timestamp}`)
                .then(res => {
                    if (res.data && res.data.success && res.data.user) {
                        const freshData = res.data.user;
                        setUser(freshData);
                        localStorage.setItem('user', JSON.stringify(freshData));
                        
                        // Keep edit form in sync with fresh data
                        setEditData(prev => ({
                            ...prev,
                            name: freshData.name || prev.name,
                            contact_no: freshData.contact_no || prev.contact_no,
                            cnic: freshData.cnic || prev.cnic,
                            wing: freshData.wing || prev.wing,
                            entry_year: freshData.entry_year || prev.entry_year,
                            leaving_year: freshData.leaving_year || prev.leaving_year,
                            current_status: freshData.current_status || prev.current_status,
                            education: freshData.education || prev.education,
                            institute: freshData.institute || prev.institute,
                            profession: freshData.profession || prev.profession,
                            job_status: freshData.job_status || prev.job_status, // <-- Added
                            organization: freshData.organization || prev.organization,
                            designation: freshData.designation || prev.designation,
                            current_address: freshData.current_address || prev.current_address,
                            permanent_address: freshData.permanent_address || prev.permanent_address,
                            skills: freshData.skills || prev.skills,
                            achievements: freshData.achievements || prev.achievements
                        }));
                    }
                })
                .catch(err => console.error("Background sync failed:", err));
            // ------------------------------------------

            API.get('/get_upcoming_events.php')
                .then(res => {
                    if (Array.isArray(res.data)) {
                        setEventCount(res.data.length);
                    }
                })
                .catch(err => console.error("Error fetching event count:", err));

            API.get('/get_alumni.php')
                .then(res => {
                    if (Array.isArray(res.data)) {
                        setAlumniCount(res.data.length);
                    }
                })
                .catch(err => console.error("Error fetching alumni count:", err));
        }
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('user');
        navigate('/login');
    };

    const getInitials = (name) => {
        if (!name) return 'C';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('profile_pic', file);
        formData.append('user_id', user.id);

        try {
            const res = await API.post('/upload_profile_pic.php', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.success) {
                const updatedUser = { ...user, profile_pic: res.data.image_url };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                alert("Profile picture updated successfully!");
            } else {
                alert(res.data.message);
            }
        } catch (err) {
            console.error(err);
            alert("Upload failed. Check console.");
        } finally {
            setUploading(false);
        }
    };

    const getProfilePicUrl = () => {
        if (!user?.profile_pic) return `https://ui-avatars.com/api/?name=${getInitials(user?.name)}&background=ffc300&color=134628&size=150`; 
        return `https://sherianalumni.com/api/${user.profile_pic}`;
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setUpdateStatus('Updating...');
        
        try {
            const updatedUser = { ...user, ...editData };
            const LOCAL_URL = 'https://sherianalumni.com/api/update_profile.php';
            
            const res = await axios.post(LOCAL_URL, updatedUser, {
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (res.data && res.data.success) {
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                setUpdateStatus('Profile Updated Successfully!');
                setTimeout(() => {
                    const isInvalid = (val) => !val || String(val).trim() === '' || String(val).toLowerCase() === 'null';
                    
                    const stillIncomplete = (
                        isInvalid(updatedUser.contact_no) || isInvalid(updatedUser.cnic) || 
                        isInvalid(updatedUser.entry_year) || isInvalid(updatedUser.leaving_year) ||
                        isInvalid(updatedUser.wing) || isInvalid(updatedUser.current_status) || 
                        isInvalid(updatedUser.education) || isInvalid(updatedUser.institute) ||
                        isInvalid(updatedUser.profession) || isInvalid(updatedUser.job_status) || // <-- Added
                        isInvalid(updatedUser.designation) || isInvalid(updatedUser.organization) || 
                        isInvalid(updatedUser.current_address) || isInvalid(updatedUser.permanent_address)
                    );

                    if (!stillIncomplete) {
                        setIsEditing(false);
                    } else {
                        setUpdateStatus('Please fill all mandatory fields to continue.');
                    }
                    setUpdateStatus('');
                }, 1500);
            } else {
                const errorMsg = res.data.error || JSON.stringify(res.data);
                setUpdateStatus('Error: ' + errorMsg);
            }
        } catch (err) {
            console.error("Axios Error:", err);
            setUpdateStatus('Network Error. Check browser console.');
        }
    };

    const handleDownloadCard = () => {
        setIsDownloading(true);
        const input = cardRef.current;

        html2canvas(input, { 
            scale: 3, 
            useCORS: true, 
            backgroundColor: null 
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('landscape', 'mm', [85.6, 54]); 
            pdf.addImage(imgData, 'PNG', 0, 0, 85.6, 54);
            pdf.save(`${user.name.replace(/\s+/g, '_')}_Membership_Card.pdf`);
            setIsDownloading(false);
        }).catch(err => {
            console.error("PDF Generation Error:", err);
            alert("Failed to generate PDF.");
            setIsDownloading(false);
        });
    };

    const handlePrintCard = () => {
        setIsPrinting(true);
        const input = cardRef.current;

        html2canvas(input, { 
            scale: 3, 
            useCORS: true, 
            backgroundColor: null 
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            
            const printWindow = window.open('', '_blank');
            printWindow.document.write(`
                <html>
                    <head>
                        <title>Print Membership Card</title>
                        <style>
                            @media print {
                                @page { size: landscape; margin: 0; }
                                body { margin: 1cm; display: flex; justify-content: center; align-items: flex-start; }
                            }
                        </style>
                    </head>
                    <body>
                        <img src="${imgData}" style="width: 85.6mm; height: 54mm; border-radius: 8px; border: 1px solid #ccc;" />
                        <script>
                            setTimeout(() => {
                                window.print();
                            }, 300); 
                        </script>
                    </body>
                </html>
            `);
            printWindow.document.close();
            setIsPrinting(false);
        }).catch(err => {
            console.error("Print Error:", err);
            alert("Failed to print card.");
            setIsPrinting(false);
        });
    };

    if (!user) return null; 

    return (
        <div className="dashboard-wrapper">
            <style>{`
                .dashboard-wrapper {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    background: #f4f7f6;
                    min-height: 100vh;
                    padding-top: 80px; 
                    padding-bottom: 60px;
                    overflow-x: hidden; 
                    width: 100vw;
                    max-width: 100%;
                }

                .dashboard-wrapper *, .dashboard-wrapper *::before, .dashboard-wrapper *::after {
                    box-sizing: border-box;
                }

                .dashboard-container, .activity-section, .profile-card, .id-card-wrapper {
                    min-width: 0; 
                    max-width: 100%;
                }

                .dashboard-header {
                    background: linear-gradient(135deg, #134628 0%, #0a2415 100%);
                    color: white;
                    padding: 60px 20px 80px;
                    text-align: center;
                    position: relative;
                    border-bottom: 5px solid #ffc300;
                    animation: fadeInDown 0.8s ease-out;
                }

                .dashboard-header h1 {
                    font-size: clamp(2rem, 5vw, 3rem);
                    font-weight: 900;
                    text-transform: uppercase;
                    margin-bottom: 10px;
                    background: linear-gradient(to right, #ffc300, #ffeaa7, #ffc300);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .dashboard-header p {
                    color: #a0c4ff;
                    font-size: 1.1rem;
                    font-weight: 300;
                }

                .dashboard-container {
                    max-width: 1200px;
                    margin: -50px auto 0; 
                    padding: 0 20px;
                    display: grid;
                    grid-template-columns: 1fr 2fr;
                    gap: 30px;
                    position: relative;
                    z-index: 10;
                    align-items: start;
                }

                .profile-card {
                    background: #ffffff;
                    border-radius: 20px;
                    box-shadow: 0 15px 35px rgba(19, 70, 40, 0.08);
                    padding: 40px 30px;
                    text-align: center;
                    border: 1px solid #f0f0f0;
                    animation: slideUpFade 0.6s ease-out backwards;
                }

                .profile-pic-wrapper { text-align: center; margin-bottom: 20px; }
                .profile-pic-container {
                    width: 130px; height: 130px; margin: 0 auto; border-radius: 50%;
                    border: 5px solid #ffc300; position: relative; overflow: hidden;
                    cursor: pointer; box-shadow: 0 10px 25px rgba(255, 195, 0, 0.3);
                    transition: 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                .profile-pic-container:hover { transform: translateY(-5px); box-shadow: 0 15px 35px rgba(255, 195, 0, 0.5); }
                .profile-pic-image { width: 100%; height: 100%; object-fit: cover; }
                .profile-pic-overlay {
                    position: absolute; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(19, 70, 40, 0.7); display: flex; align-items: center;
                    justify-content: center; opacity: 0; transition: 0.3s; color: #ffc300; font-size: 2rem;
                }
                .profile-pic-container:hover .profile-pic-overlay { opacity: 1; }

                .profile-card h3 { color: #134628; font-weight: 800; font-size: 1.5rem; margin-bottom: 5px; }

                .badge-wing {
                    display: inline-block; background: #134628; color: #ffc300;
                    padding: 5px 15px; border-radius: 50px; font-size: 0.85rem;
                    font-weight: 700; text-transform: uppercase; margin-bottom: 25px; letter-spacing: 1px;
                }

                .profile-details { text-align: left; margin-bottom: 30px; }
                .detail-row { display: flex; align-items: center; padding: 12px 0; border-bottom: 1px solid #f0f2f5; }
                .detail-row:last-child { border-bottom: none; }
                .detail-row i { color: #ffc300; width: 30px; font-size: 1.1rem; }
                .detail-row span { color: #475569; font-weight: 600; font-size: 0.95rem; }

                .career-section { margin-top: 20px; padding-top: 20px; border-top: 2px dashed #f0f2f5; }
                .section-subtitle { font-size: 0.8rem; text-transform: uppercase; color: #94a3b8; font-weight: 800; letter-spacing: 1px; margin-bottom: 10px; display: block; }

                .btn-edit-profile {
                    width: 100%; background: #f8fafc; color: #134628; border: 1px solid #e2e8f0;
                    padding: 12px; border-radius: 12px; font-weight: 700; cursor: pointer; transition: 0.3s; margin-bottom: 15px;
                }
                .btn-edit-profile:hover { background: #134628; color: #ffc300; border-color: #134628; }

                .id-card-wrapper {
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 40px 30px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.03);
                    border: 1px solid #f0f0f0;
                    text-align: center;
                    animation: slideUpFade 1s ease-out backwards 0.6s;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    overflow: hidden; 
                }
                
                .id-card-title {
                    font-size: 1.2rem; color: #134628; font-weight: 900; margin-bottom: 25px; text-transform: uppercase; letter-spacing: 1px;
                }
                
                .id-card-canvas {
                    width: 360px; 
                    height: 220px;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 15px 35px rgba(0,0,0,0.1);
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    text-align: left;
                    border: 1px solid #e2e8f0;
                    flex-shrink: 0; 
                    transform-origin: top center;
                }
                
                .id-card-green-bar {
                    width: 25px;
                    background: #10b981; 
                    height: 100%;
                }
                
                .id-card-content {
                    flex: 1;
                    padding: 15px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                .id-card-header { display: flex; align-items: center; gap: 10px; }
                
                .id-card-logo {
                    width: 45px; height: 45px; 
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                
                .id-card-header-text h4 { color: #134628; font-size: 0.85rem; font-weight: 900; margin: 0; line-height: 1.1; letter-spacing: 0.5px; }
                .id-card-header-text p { color: #d90429; font-size: 0.55rem; font-weight: 700; margin: 2px 0 0 0; text-transform: uppercase; }

                .id-card-badge {
                    background: #10b981; color: white; padding: 4px 10px; border-radius: 20px;
                    font-size: 0.65rem; font-weight: 700; display: inline-block; text-transform: uppercase;
                    margin: 10px 0; align-self: flex-start;
                }

                .id-card-body { display: flex; gap: 15px; align-items: flex-end; }

                .id-card-photo {
                    width: 75px; height: 95px; border-radius: 8px; object-fit: cover;
                    border: 2px solid #000; background: #f0f0f0;
                }

                .id-card-info { flex: 1; padding-bottom: 5px; }
                .id-card-info h3 { color: #134628; font-size: 1.1rem; font-weight: 900; margin: 0 0 8px 0; line-height: 1; text-transform: capitalize; }
                .id-card-info p { color: #333; font-size: 0.62rem; margin: 0 0 4px 0; font-weight: 600; line-height: 1.2; }
                .id-card-info strong { color: #64748b; font-weight: 500; }

                .card-actions {
                    display: flex;
                    justify-content: center;
                    gap: 15px;
                    margin-top: 25px;
                    max-width: 400px;
                    margin-left: auto;
                    margin-right: auto;
                }
                .btn-download-pdf, .btn-print-card {
                    flex: 1;
                    color: white;
                    border: none;
                    padding: 12px;
                    border-radius: 12px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: 0.3s;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.9rem;
                }
                
                .btn-download-pdf { background: #10b981; }
                .btn-download-pdf:hover { background: #059669; transform: translateY(-2px); box-shadow: 0 8px 15px rgba(16, 185, 129, 0.3); }
                .btn-download-pdf:disabled { background: #94a3b8; cursor: not-allowed; transform: none; box-shadow: none; }

                .btn-print-card { background: #134628; }
                .btn-print-card:hover { background: #0c2d19; transform: translateY(-2px); box-shadow: 0 8px 15px rgba(19, 70, 40, 0.3); color: #ffc300; }
                .btn-print-card:disabled { background: #94a3b8; cursor: not-allowed; transform: none; box-shadow: none; }

                .activity-section { display: flex; flex-direction: column; gap: 30px; }

                .welcome-card { background: linear-gradient(135deg, #134628 0%, #0a2415 100%); border-radius: 20px; padding: 40px; color: white; box-shadow: 0 15px 35px rgba(19, 70, 40, 0.1); animation: slideUpFade 0.8s ease-out backwards 0.2s; }
                .welcome-card h2 { color: #ffc300; font-weight: 800; margin-bottom: 10px; }

                .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; animation: slideUpFade 1s ease-out backwards 0.4s; }
                .stat-box { background: #ffffff; border-radius: 20px; padding: 30px; text-align: center; border: 1px solid #f0f0f0; box-shadow: 0 10px 25px rgba(0,0,0,0.03); transition: all 0.3s ease; }
                .stat-box:hover { transform: translateY(-8px); box-shadow: 0 15px 35px rgba(255, 195, 0, 0.15); border-color: #ffc300; }
                .stat-box.clickable { cursor: pointer; }
                .stat-box i { font-size: 2.5rem; color: #e9ecef; margin-bottom: 15px; display: block; transition: 0.3s; }
                .stat-box:hover i { color: #ffc300; }
                .stat-box h4 { color: #134628; font-weight: 800; margin-bottom: 5px; }
                .stat-box p { color: #888; font-size: 0.9rem; margin: 0; }

                /* MODAL SCROLL AND EXPANDED STYLING */
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(19, 70, 40, 0.8); backdrop-filter: blur(5px); z-index: 1000; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.3s; }
                
                .modal-content { 
                    background: white; border-radius: 24px; padding: 40px; width: 90%; max-width: 650px; 
                    box-shadow: 0 25px 50px rgba(0,0,0,0.3); animation: slideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1); 
                    position: relative; max-height: 85vh; overflow-y: auto; 
                }
                
                .modal-content::-webkit-scrollbar { width: 8px; }
                .modal-content::-webkit-scrollbar-thumb { background: #134628; border-radius: 4px; }
                .modal-content::-webkit-scrollbar-track { background: #f0f2f5; border-radius: 4px; }

                .close-modal { position: absolute; top: 20px; right: 25px; background: none; border: none; font-size: 1.5rem; color: #a0aec0; cursor: pointer; transition: 0.3s; z-index: 10; }
                .close-modal:hover { color: #d90429; }
                
                .form-section-title { font-size: 1.1rem; color: #134628; font-weight: 800; border-bottom: 2px solid #ffc300; padding-bottom: 8px; margin-top: 25px; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 1px; }

                .edit-form-group { margin-bottom: 20px; text-align: left; }
                .edit-form-group label { display: block; font-weight: 700; color: #134628; margin-bottom: 8px; font-size: 0.9rem; }
                
                .edit-form-group input, 
                .edit-form-group select, 
                .edit-form-group textarea { 
                    width: 100%; 
                    padding: 12px 15px; 
                    border: 2px solid #e2e8f0; 
                    border-radius: 10px; 
                    font-size: 1rem; 
                    outline: none; 
                    transition: 0.3s; 
                    font-family: inherit; 
                }
                .edit-form-group input:focus, 
                .edit-form-group select:focus, 
                .edit-form-group textarea:focus { 
                    border-color: #ffc300; 
                    box-shadow: 0 0 0 4px rgba(255, 195, 0, 0.1); 
                }
                
                .btn-save { background: linear-gradient(45deg, #ffc300, #ffb703); color: #134628; border: none; padding: 15px; width: 100%; border-radius: 10px; font-weight: 800; font-size: 1.1rem; text-transform: uppercase; cursor: pointer; transition: 0.3s; margin-top: 20px; }
                .btn-save:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(255, 195, 0, 0.3); }

                .form-responsive-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 15px;
                }

                @media (max-width: 992px) {
                    .dashboard-container { grid-template-columns: 1fr; margin-top: -30px; }
                    .dashboard-header { padding: 40px 20px 60px; }
                }

                @media (max-width: 480px) {
                    .dashboard-header h1 { font-size: 2.2rem; }
                    .profile-card { padding: 30px 20px; }
                    
                    .stats-grid { grid-template-columns: 1fr; }
                    .welcome-card { padding: 30px 20px; }
                    
                    .id-card-wrapper { padding: 30px 15px; }
                    .id-card-canvas {
                        transform: scale(0.85);
                        margin-bottom: -30px; 
                    }
                    
                    .card-actions { flex-direction: column; width: 100%; margin-top: 15px; } 
                    .btn-download-pdf, .btn-print-card { width: 100%; }
                    
                    .modal-content { padding: 30px 20px; }
                    
                    .form-responsive-grid { 
                        grid-template-columns: 1fr; 
                        gap: 10px; 
                    }
                }

                @media (max-width: 360px) {
                    .id-card-canvas {
                        transform: scale(0.75);
                        margin-bottom: -50px;
                    }
                }

                @keyframes fadeInDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideUpFade { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            <div className="dashboard-header">
                <h1>ALUMNI PORTAL</h1>
                <p>Manage your Sherian profile and connections</p>
            </div>

            <div className="dashboard-container">
                {/* LEFT COLUMN: PROFILE DETAILS */}
                <div className="profile-card">
                    
                    <div className="profile-pic-wrapper">
                        <div className="profile-pic-container" onClick={() => fileInputRef.current.click()}>
                            <img 
                                src={getProfilePicUrl()} 
                                alt="Alumni Profile" 
                                className="profile-pic-image" 
                            />
                            <div className="profile-pic-overlay">
                                {uploading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-camera"></i>}
                            </div>
                        </div>
                        <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImageUpload} />
                    </div>

                    <h3>{user.name}</h3>
                    <span className="badge-wing">{user.wing || 'Sherian'} Wing</span>

                    <div className="profile-details">
                        <div className="detail-row"><i className="fas fa-envelope"></i><span>{user.email}</span></div>
                        <div className="detail-row"><i className="fas fa-id-badge"></i><span>Kit No: {user.kit_number || 'N/A'}</span></div>
                        
                        {(user.contact_no) && (
                            <div className="detail-row"><i className="fas fa-phone"></i><span>{user.contact_no}</span></div>
                        )}
                        {(user.entry_batch) && (
                            <div className="detail-row"><i className="fas fa-layer-group"></i><span>{user.entry_batch}</span></div>
                        )}

                        {(user.job_status || user.organization || user.profession || user.institute) && (
                            <div className="career-section">
                                <span className="section-subtitle">Academic & Professional Info</span>
                                {user.institute && (
                                    <div className="detail-row"><i className="fas fa-university"></i><span>{user.institute}</span></div>
                                )}
                                {user.profession && (
                                    <div className="detail-row"><i className="fas fa-user-tie"></i><span>{user.profession}</span></div>
                                )}
                                {user.organization && (
                                    <div className="detail-row"><i className="fas fa-building"></i><span>{user.organization}</span></div>
                                )}
                            </div>
                        )}
                    </div>

                    <button className="btn-edit-profile" onClick={() => setIsEditing(true)}>
                        <i className="fas fa-user-edit me-2"></i> Edit Profile
                    </button>
                </div>

                {/* RIGHT COLUMN: WELCOME, STATS, AND ID CARD */}
                <div className="activity-section">
                    <div className="welcome-card">
                        <h2>Welcome back, {user.name.split(' ')[0]}!</h2>
                        <p>Your Sherian network is growing. Stay updated with the latest college events and connect with your batchmates.</p>
                    </div>

                    <div className="stats-grid">
                        <div className="stat-box clickable" onClick={() => navigate('/#grand-gatherings')} >
                            <i className="fas fa-ticket-alt"></i><h4>{eventCount}</h4><p>Current Events</p>
                        </div>
                        <div className="stat-box clickable" onClick={() => navigate('/search')}>
                            <i className="fas fa-users"></i><h4>{alumniCount}</h4><p>Connect Alumni</p>
                        </div>
                        <div className="stat-box clickable" onClick={() => navigate('/elections')}>
                            <i className="fas fa-vote-yea" style={{color: '#6c5ce7'}}></i><h4>Decisions</h4><p>Voting Portal</p>
                        </div>
                    </div>

                    <div className="id-card-wrapper">
                        <h4 className="id-card-title">Digital Membership Card</h4>
                        
                        <div className="id-card-canvas" id="alumni-id-card" ref={cardRef}>
                            <div className="id-card-green-bar"></div>
                            <div className="id-card-content">
                                <div className="id-card-header">
                                <div className="id-card-logo">
                                <img 
                                src="https://sherianalumni.com/logo.jpeg" 
                                alt="Sherian Logo" 
                                style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} 
                            />
                        </div>
                        <div className="id-card-header-text">
                            <h4>SHERIANS ALUMNI ASSOCIATION</h4>
                            <p>Karnal Sher Khan Cadet College Swabi</p>
                        </div>
                    </div>
                                <div className="id-card-badge">Membership Card</div>
                                <div className="id-card-body">
                                    <img src={getProfilePicUrl()} alt="User" className="id-card-photo" />
                                    <div className="id-card-info">
                                        <h3>{user.name}</h3>
                                        <p><strong>Kit / Membership No:</strong> {user.kit_number || user.id}</p>
                                        <p><strong>Entry / Batch:</strong> {user.entry_batch || 'N/A'}</p>
                                        <p><strong>Contact No:</strong> {user.contact_no || 'N/A'}</p>
                                        <p><strong>CNIC:</strong> {user.cnic || 'N/A'}</p> 
                                        <p><strong>Valid upto:</strong> 01-04-2030</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-actions">
                            <button className="btn-download-pdf" onClick={handleDownloadCard} disabled={isDownloading || isPrinting}>
                                {isDownloading ? <><i className="fas fa-spinner fa-spin"></i> PDF...</> : <><i className="fas fa-file-pdf"></i> Download PDF</>}
                            </button>

                            <button className="btn-print-card" onClick={handlePrintCard} disabled={isDownloading || isPrinting}>
                                {isPrinting ? <><i className="fas fa-spinner fa-spin"></i> Loading...</> : <><i className="fas fa-print"></i> Print Card</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- EXPANDED EDIT PROFILE MODAL --- */}
            {isEditing && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        {/* CONDITIONAL CLOSE BUTTON */}
                        {!isProfileIncomplete && (
                            <button className="close-modal" onClick={() => setIsEditing(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                        
                        <h2 style={{color: '#134628', fontWeight: '900', textAlign: 'center', textTransform: 'uppercase'}}>
                            {isProfileIncomplete ? 'Action Required' : 'Update Profile'}
                        </h2>
                        
                        <p style={{textAlign: 'center', color: isProfileIncomplete ? '#d90429' : '#64748b', marginBottom: '25px', fontSize: '0.9rem', fontWeight: isProfileIncomplete ? 'bold' : 'normal'}}>
                            {isProfileIncomplete 
                                ? 'Please complete your entire profile (all * fields are mandatory) to unlock dashboard access.' 
                                : 'Update your academic and professional journey.'}
                        </p>
                        
                        <form onSubmit={handleUpdateProfile}>
                            <h3 className="form-section-title">Personal Information</h3>
                            <div className="edit-form-group">
                                <label>Full Name <span style={{color: 'red'}}>*</span></label>
                                <input type="text" value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} required />
                            </div>
                            
                            <div className="form-responsive-grid">
                                <div className="edit-form-group">
                                    <label>Contact Number <span style={{color: 'red'}}>*</span></label>
                                    <input type="text" placeholder="e.g. 03001234567" value={editData.contact_no} onChange={(e) => setEditData({...editData, contact_no: e.target.value})} required />
                                </div>
                                <div className="edit-form-group">
                                    <label>CNIC <span style={{color: 'red'}}>*</span></label>
                                    <input type="text" placeholder="e.g. 15501-1234567-1" value={editData.cnic} onChange={(e) => setEditData({...editData, cnic: e.target.value})} required />
                                </div>
                            </div>

                            <h3 className="form-section-title">College Details</h3>
                            <div className="form-responsive-grid">
                                <div className="edit-form-group">
                                    <label>Joining Year <span style={{color: 'red'}}>*</span></label>
                                    <input type="number" placeholder="e.g. 2018" value={editData.entry_year} onChange={(e) => setEditData({...editData, entry_year: e.target.value})} required />
                                </div>
                                <div className="edit-form-group">
                                    <label>Leaving Year <span style={{color: 'red'}}>*</span></label>
                                    <input type="number" placeholder="e.g. 2023" value={editData.leaving_year} onChange={(e) => setEditData({...editData, leaving_year: e.target.value})} required />
                                </div>
                            </div>
                            
                            <div className="edit-form-group">
                                <label>Wing <span style={{color: 'red'}}>*</span></label>
                                <select value={editData.wing} onChange={(e) => setEditData({...editData, wing: e.target.value})} required>
                                    <option value="" disabled>Select Wing</option>
                                    <option value="Iqbal">Iqbal</option>
                                    <option value="Jinnah">Jinnah</option>
                                    <option value="Haider">Haider</option>
                                    <option value="Umar">Umar</option>
                                    <option value="Qasim">Qasim</option>
                                    <option value="Johar">Johar</option>
                                    <option value="Tipu">Tipu</option>
                                    <option value="Khalid">Khalid</option>
                                </select>
                            </div>

                            <h3 className="form-section-title">Academic & Professional</h3>
                            <div className="form-responsive-grid">
                                <div className="edit-form-group">
                                    <label>Current Status <span style={{color: 'red'}}>*</span></label>
                                    <select value={editData.current_status} onChange={(e) => setEditData({...editData, current_status: e.target.value})} required>
                                        <option value="" disabled>Select Status</option>
                                        <option value="Student">Student</option>
                                        <option value="Job">Job / Professional</option>
                                    </select>
                                </div>
                                <div className="edit-form-group">
                                    <label>Highest Education <span style={{color: 'red'}}>*</span></label>
                                    <input type="text" placeholder="e.g. Bachelors in CS" value={editData.education} onChange={(e) => setEditData({...editData, education: e.target.value})} required />
                                </div>
                            </div>

                            <div className="edit-form-group">
                                <label>Institute / University <span style={{color: 'red'}}>*</span></label>
                                <input type="text" placeholder="e.g. NUST, FAST..." value={editData.institute} onChange={(e) => setEditData({...editData, institute: e.target.value})} required />
                            </div>

                            <div className="form-responsive-grid">
                                <div className="edit-form-group">
                                    <label>Profession <span style={{color: 'red'}}>*</span></label>
                                    <input type="text" placeholder="e.g. Engineer, Doctor" value={editData.profession} onChange={(e) => setEditData({...editData, profession: e.target.value})} required />
                                </div>
                                {/* --- NEW JOB TITLE FIELD --- */}
                                <div className="edit-form-group">
                                    <label>Job Title <span style={{color: 'red'}}>*</span></label>
                                    <input type="text" placeholder="e.g. Lead Engineer, Consultant" value={editData.job_status} onChange={(e) => setEditData({...editData, job_status: e.target.value})} required />
                                </div>
                            </div>

                            <div className="form-responsive-grid">
                                <div className="edit-form-group">
                                    <label>Designation <span style={{color: 'red'}}>*</span></label>
                                    <input type="text" placeholder="e.g. BPS-17, Senior Grade" value={editData.designation} onChange={(e) => setEditData({...editData, designation: e.target.value})} required />
                                </div>
                                <div className="edit-form-group">
                                    <label>Organization / Company Name <span style={{color: 'red'}}>*</span></label>
                                    <input type="text" placeholder="e.g. Google, Pakistan Army..." value={editData.organization} onChange={(e) => setEditData({...editData, organization: e.target.value})} required />
                                </div>
                            </div>

                            <h3 className="form-section-title">Contact Information</h3>
                            <div className="edit-form-group">
                                <label>Current Address <span style={{color: 'red'}}>*</span></label>
                                <input type="text" placeholder="House #, Street, City" value={editData.current_address} onChange={(e) => setEditData({...editData, current_address: e.target.value})} required />
                            </div>
                            <div className="edit-form-group">
                                <label>Permanent Address <span style={{color: 'red'}}>*</span></label>
                                <input type="text" placeholder="House #, Street, City" value={editData.permanent_address} onChange={(e) => setEditData({...editData, permanent_address: e.target.value})} required />
                            </div>

                            {/* --- NEW OPTIONAL SECTION --- */}
                            <h3 className="form-section-title">Other Information (Optional)</h3>
                            <div className="edit-form-group">
                                <label>Key Skills</label>
                                <input type="text" placeholder="e.g. React, Project Management, Public Speaking" value={editData.skills} onChange={(e) => setEditData({...editData, skills: e.target.value})} />
                            </div>
                            <div className="edit-form-group">
                                <label>Achievements & Awards</label>
                                <textarea 
                                    placeholder="List any notable achievements, awards, or publications..." 
                                    value={editData.achievements} 
                                    onChange={(e) => setEditData({...editData, achievements: e.target.value})} 
                                    rows="3" 
                                ></textarea>
                            </div>

                            {updateStatus && (
                                <div style={{textAlign: 'center', marginBottom: '15px', fontWeight: 'bold', color: updateStatus.includes('Success') ? '#2a9d8f' : '#d90429'}}>
                                    {updateStatus}
                                </div>
                            )}

                            <button type="submit" className="btn-save">Save Profile Details <i className="fas fa-check ms-2"></i></button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlumniDashboard;